import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';

interface HLSPlayerProps {
  url: string;
  autoPlay?: boolean;
  controls?: boolean;
  muted?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onError?: (error: Error) => void;
}

const HLSPlayer: React.FC<HLSPlayerProps> = ({
  url,
  autoPlay = false,
  controls = true,
  muted = false,
  className = '',
  style = {},
  onError,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(muted);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    let hls: any = null;
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    console.log('[HLSPlayer] Loading URL:', url);

    const handleLoadedMetadata = () => {
      console.log('[HLSPlayer] ✅ Metadata loaded successfully');
      if (isMounted) {
        setIsLoading(false);
        if (autoPlay) {
          video.play().catch((err) => {
            console.error('[HLSPlayer] Autoplay failed:', err);
            setError('Autoplay blocked. Click play to start.');
          });
        }
      }
    };

    const handleCanPlay = () => {
      console.log('[HLSPlayer] ✅ Can play event fired');
      if (isMounted) {
        setIsLoading(false);
      }
    };

    const handleVideoError = (e: Event) => {
      console.error('[HLSPlayer] ❌ Video element error:', e);
      console.error('[HLSPlayer] Video error details:', video.error);
      if (isMounted) {
        let errorMessage = 'Failed to load video stream';
        
        if (video.error) {
          if (video.error.code === 4) {
            // MediaError code 4 = MEDIA_ERR_SRC_NOT_SUPPORTED
            // Often caused by stream encryption or server-side errors
            errorMessage = 'Stream not supported. This may be due to:\n• Stream encryption enabled (try entering decryption code)\n• Camera offline or stream unavailable\n• Invalid stream format';
          } else {
            errorMessage = `Video error (code ${video.error.code}): ${video.error.message || 'Unknown error'}`;
          }
        }
        
        setError(errorMessage);
        setIsLoading(false);
      }
    };

    const handleLoadStart = () => {
      console.log('[HLSPlayer] 🔄 Load started');
    };

    const handleProgress = () => {
      console.log('[HLSPlayer] 📊 Loading progress...');
    };

    const loadVideo = async () => {
      try {
        // Set a timeout to detect stuck loading
        const loadTimeout = setTimeout(() => {
          if (isMounted) {
            console.error('[HLSPlayer] ⏱️ Loading timeout - stream not responding');
            setError('Stream loading timeout. The camera may be offline or the stream URL expired.');
            setIsLoading(false);
          }
        }, 30000); // 30 second timeout

        // Check if HLS is natively supported (Safari)
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          console.log('[HLSPlayer] Using native HLS support (Safari)');
          
          // Pre-check the m3u8 file for server errors
          try {
            const response = await fetch(url);
            const m3u8Content = await response.text();
            console.log('[HLSPlayer] M3U8 content preview:', m3u8Content.substring(0, 200));
            
            // Check for error codes in the manifest
            if (m3u8Content.includes('ErrCode/')) {
              const errMatch = m3u8Content.match(/ErrCode\/(\d+)/);
              const errCode = errMatch ? errMatch[1] : 'Unknown';
              clearTimeout(loadTimeout);
              
              let errorMsg = `Server error ${errCode}`;
              if (errCode === '9053') {
                errorMsg = 'Stream unavailable (Error 9053). Possible causes:\n' +
                           '• Camera is offline or not streaming\n' +
                           '• Recording encryption enabled (provide code parameter)\n' +
                           '• Insufficient permissions or resource mismatch\n' +
                           '• Channel configuration issue';
              } else if (errCode === '5405') {
                errorMsg = 'Camera not streaming (Error 5405). This usually means:\n' +
                           '• Camera is online but video feed is not active\n' +
                           '• Camera needs to be restarted\n' +
                           '• Video encoding settings incompatible with HLS (must be H.264)\n' +
                           '• Try accessing camera directly via web interface first\n' +
                           '• Check camera settings: Configuration → Video/Audio → Video';
              }
              
              console.error('[HLSPlayer] Server error detected:', errorMsg);
              console.error('[HLSPlayer] Full m3u8 content:', m3u8Content);
              setError(errorMsg);
              setIsLoading(false);
              return;
            }
          } catch (fetchErr) {
            console.warn('[HLSPlayer] Could not pre-check m3u8:', fetchErr);
            // Continue anyway
          }
          
          // Add all event listeners
          video.addEventListener('loadedmetadata', handleLoadedMetadata);
          video.addEventListener('canplay', handleCanPlay);
          video.addEventListener('error', handleVideoError);
          video.addEventListener('loadstart', handleLoadStart);
          video.addEventListener('progress', handleProgress);
          
          // When metadata loads, clear timeout
          video.addEventListener('loadedmetadata', () => clearTimeout(loadTimeout), { once: true });
          
          // Set the source and load
          console.log('[HLSPlayer] Setting video source...');
          video.src = url;
          video.load();
          console.log('[HLSPlayer] Video.load() called');
        } else {
          console.log('[HLSPlayer] Using hls.js for playback');
          clearTimeout(loadTimeout);
          // For other browsers, use hls.js
          const Hls = (window as any).Hls;
          
          const initHls = () => {
            const Hls = (window as any).Hls;
          if (Hls && Hls.isSupported()) {
            console.log('[HLSPlayer] Initializing hls.js');
            hls = new Hls({
              enableWorker: true,
              lowLatencyMode: true,
              backBufferLength: 90,
              xhrSetup: function(xhr: XMLHttpRequest, url: string) {
                console.log('[HLSPlayer] XHR request to:', url);
                // CORS handling - the server must support CORS
              }
            });

            hls.loadSource(url);
            hls.attachMedia(video!);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              console.log('[HLSPlayer] Manifest parsed successfully');
              setIsLoading(false);
              if (autoPlay) {
                video!.play().catch((err: Error) => {
                  console.error('[HLSPlayer] Autoplay failed:', err);
                  setError('Autoplay blocked. Click play to start.');
                });
              }
            });

            hls.on(Hls.Events.ERROR, (event: any, data: any) => {
              console.error('[HLSPlayer] HLS error:', data);
              if (data.fatal) {
                const errorMsg = `${data.type}: ${data.details}${data.response?.code ? ` (HTTP ${data.response.code})` : ''}`;
                console.error('[HLSPlayer] Fatal error:', errorMsg);
                setError(errorMsg);
                setIsLoading(false);
                if (onError) {
                  onError(new Error(errorMsg));
                }
                
                // Try to recover from errors
                switch (data.type) {
                  case Hls.ErrorTypes.NETWORK_ERROR:
                    console.log('[HLSPlayer] Fatal network error, trying to recover...');
                    hls.startLoad();
                    break;
                  case Hls.ErrorTypes.MEDIA_ERROR:
                    console.log('[HLSPlayer] Fatal media error, trying to recover...');
                    hls.recoverMediaError();
                    break;
                  default:
                    console.log('[HLSPlayer] Unrecoverable error, destroying player');
                    hls.destroy();
                    break;
                }
              }
            });
          } else {
            console.error('[HLSPlayer] HLS is not supported in this browser');
            setError('HLS is not supported in this browser');
            setIsLoading(false);
          }
        };
        
        if (!Hls) {
          // Dynamically load hls.js if not available
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
          script.async = true;
          script.onload = () => {
            initHls();
          };
          script.onerror = () => {
            setError('Failed to load HLS player library');
            setIsLoading(false);
          };
          document.body.appendChild(script);
        } else {
          initHls();
        }
      }
      } catch (err) {
        console.error('Video loading error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load video');
        setIsLoading(false);
        if (onError && err instanceof Error) {
          onError(err);
        }
      }
    };

    loadVideo();

    // Cleanup
    return () => {
      isMounted = false;
      
      if (hls) {
        hls.destroy();
        hls = null;
      }
      
      if (video) {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleVideoError);
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('progress', handleProgress);
        video.pause();
        video.removeAttribute('src');
        video.load(); // Reset the video element
      }
    };
  }, [url, autoPlay, onError]);

  // Control handlers
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = vol;
      setVolume(vol);
      setIsMuted(vol === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  };

  const changePlaybackSpeed = (rate: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
  };

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (!document.fullscreenElement) {
        containerRef.current.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Add video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleDurationChange = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVolumeUpdate = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('volumechange', handleVolumeUpdate);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('volumechange', handleVolumeUpdate);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`relative ${className}`} 
      style={style}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(isPlaying ? false : true)}
    >
      <video
        ref={videoRef}
        controls={false}
        muted={muted}
        playsInline
        className="w-full h-full bg-black"
        style={{ maxWidth: '100%', ...style }}
        onClick={togglePlayPause}
      />
      
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-white text-sm">Loading stream...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute top-4 left-4 right-4 bg-red-500 bg-opacity-90 text-white px-4 py-3 rounded z-20">
          <p className="text-sm font-semibold mb-2 whitespace-pre-line">{error}</p>
          <button
            onClick={() => window.open(url, '_blank')}
            className="text-xs bg-white text-red-600 px-3 py-1 rounded hover:bg-gray-100 transition-colors"
          >
            Open Stream URL in New Tab
          </button>
        </div>
      )}

      {/* Custom Controls */}
      {controls && (
        <div 
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-4 transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}
          style={{ pointerEvents: showControls || !isPlaying ? 'auto' : 'none' }}
        >
          {/* Seek Bar */}
          <div className="mb-3">
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:border-0"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / duration) * 100}%, #4b5563 ${(currentTime / duration) * 100}%, #4b5563 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-300 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Play/Pause */}
              <button
                onClick={togglePlayPause}
                className="text-white hover:text-blue-400 transition-colors"
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2 group">
                <button
                  onClick={toggleMute}
                  className="text-white hover:text-blue-400 transition-colors"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-0 group-hover:w-20 transition-all duration-300 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2 [&::-webkit-slider-thumb]:h-2 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-moz-range-thumb]:w-2 [&::-moz-range-thumb]:h-2 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0"
                />
              </div>

              {/* Playback Speed */}
              <div className="flex items-center gap-1">
                {[0.5, 1, 1.5, 2].map((rate) => (
                  <button
                    key={rate}
                    onClick={() => changePlaybackSpeed(rate)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      playbackRate === rate
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title={`${rate}x speed`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            </div>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="text-white hover:text-blue-400 transition-colors"
              title="Fullscreen"
            >
              <Maximize size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HLSPlayer;
